import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import { PhWarning } from 'ember-phosphor-icons';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';

export default class ImpersonationBanner extends Component {
  @service declare impersonation: ImpersonationService;
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked timeRemaining = '';

  /** Ember owns the interval's teardown, so a torn-down banner cannot tick on. */
  countdown = modifier(() => {
    this.#tick();
    const id = setInterval(() => this.#tick(), 1000);
    return () => clearInterval(id);
  });

  #tick() {
    const expireAt = this.session.expireAt;
    if (!expireAt) {
      this.timeRemaining = '';
      return;
    }
    const diff = Math.max(0, Math.floor((expireAt - Date.now()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    this.timeRemaining = `${mins}:${String(secs).padStart(2, '0')}`;
  }

  get personName() {
    return this.sessionContext.person?.fullName || this.intl.t('impersonation.unnamed_person');
  }

  endSession = () => {
    this.router.transitionTo('logout');
  };

  <template>
    {{#if this.impersonation.isImpersonated}}
      <div
        class="sticky top-0 z-[60] tio-bg-warning-soft border-b tio-border-warning px-4 py-2 tio-row tio-text-sm-normal tio-fg-warning"
        data-test-impersonation-banner
        {{this.countdown}}
      >
        <PhWarning @size={{18}} @weight="fill" class="tio-fg-warning shrink-0" />

        {{! The viewed person's name is the PII here, and session replay masks on this class. }}
        <span class="flex-1 pii" role="alert">
          {{t
            "impersonation.banner_message"
            personName=this.personName
            impersonatorId=this.impersonation.impersonatorPersonId
          }}
        </span>

        {{#if this.timeRemaining}}
          {{! Outside the alert and hidden from assistive tech: a value that changes
              every second would otherwise be announced every second. }}
          <span
            class="font-mono tio-text-xs-semibold tio-fg-warning tio-bg-warning-soft px-2 py-1 rounded"
            aria-hidden="true"
            data-test-impersonation-countdown
          >
            {{this.timeRemaining}}
          </span>
        {{/if}}

        <Button
          @appearance="outlined"
          @intent="danger"
          @size="sm"
          @testHook="impersonation-banner__end-session"
          {{on "click" this.endSession}}
          data-test-impersonation-end-session
        >
          {{t "impersonation.end_session"}}
        </Button>
      </div>

      <ConfirmationDialog
        @isOpen={{this.impersonation.showMutationConfirm}}
        @title={{t "impersonation.confirm_mutation_title"}}
        @intent="warning"
        @confirmText={{t "impersonation.confirm_proceed"}}
        @cancelText={{t "impersonation.confirm_cancel"}}
        @onConfirm={{this.impersonation.handleConfirm}}
        @onCancel={{this.impersonation.handleCancel}}
      >
        {{t "impersonation.confirm_mutation_body"}}
      </ConfirmationDialog>
    {{/if}}
  </template>
}
